import { BasiqApi } from "./basiq.api";
import {
  BasiqConnection,
  BasiqConnectionResponse,
  BasiqConnectionsResponse,
  GlossBasiqTokenResponse,
  GlossBasiqUserResponse,
  GlossConnectionRefreshResponse,
  GlossConsentResponse,
  GlossGetBasiqJobResponse,
} from "../../../../models/types/basiq.types";
import { Injectable } from "@angular/core";
import { BasiqConfigType } from "@bitwarden/web-vault/app/models/types/environement-config.type";

const basiqConfig = process.env.BASIQ as unknown as BasiqConfigType;

@Injectable({
  providedIn: "root",
})
export class BasiqAuthApi extends BasiqApi {
  async connections(): Promise<BasiqConnectionsResponse> {
    try {
      const data = { provider: "basiq", endpoint: "connections" };
      return await this.getProxyGlossApi(data);
    } catch (error) {
      await this.glossSyncService.logError([], error, `Retrieving connections from Basiq`, null);
      throw error;
    }
  }

  async accountConnections(institutionBasiqId: string): Promise<BasiqConnectionsResponse> {
    try {
      const data = {
        provider: "BASIQ",
        institutionId: institutionBasiqId,
        endpoint: "accountConnections",
      };
      return await this.getProxyGlossApi(data);
    } catch (error) {
      await this.glossSyncService.logError([], error, `Checking connections(links to banks)`, null);
      throw error;
    }
  }

  async removeConsent(consentId: string): Promise<boolean> {
    try {
      const data = {
        provider: "BASIQ",
        endpoint: "removeConsent",
        consentId,
      };
      return await this.getProxyGlossApi(data);
    } catch (error) {
      await this.glossSyncService.logError([], error, `Removing Consent`, null);
      throw error;
    }
  }

  async connectionById(connectionId: string): Promise<BasiqConnectionResponse> {
    try {
      const data = { connectionId, endpoint: "connection" };
      return await this.getProxyGlossApi(data);
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error,
        `Retrieving Connection with id :${connectionId}`,
        null,
      );
      throw error;
    }
  }

  async job(jobId: string): Promise<GlossGetBasiqJobResponse> {
    try {
      const data = { endpoint: `jobs`, jobId };
      return await this.getProxyGlossApi(data);
    } catch (error) {
      await this.glossSyncService.logError([], error, "getting job from basiq", null);
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
      return null;
    }
  }

  async consent(): Promise<GlossConsentResponse> {
    try {
      const data = { provider: "BASIQ", action: "GET", endpoint: "consents" };
      return await this.getProxyGlossApi(data);
    } catch (error) {
      await this.glossSyncService.logError([], error, "getting consent from basiq", null);
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
      return null;
    }
  }

  async createUser(): Promise<GlossBasiqUserResponse> {
    try {
      const path = basiqConfig.endpoint.user;
      return await this.dataRepositoryService.send("PUT", path, null, true, true, this.glossApiUrl);
    } catch (error) {
      await this.glossSyncService.logError([], error, "creating user in basiq", null);
      throw error;
    }
  }

  async getUser(): Promise<GlossBasiqUserResponse> {
    try {
      const path = basiqConfig.endpoint.user;
      return await this.dataRepositoryService.send("GET", path, null, true, true, this.glossApiUrl);
    } catch (error) {
      await this.glossSyncService.logError([], error, "retrieving user from basiq", null);
      throw error;
    }
  }

  async refreshConnection(connection: BasiqConnection): Promise<GlossConnectionRefreshResponse> {
    try {
      const data = {
        provider: "BASIQ",
        connectionId: connection.id,
        action: "POST",
        endpoint: "refreshConnection",
      };
      return await this.getProxyGlossApi(data);
    } catch (error) {
      await this.glossSyncService.logError(
        [],
        error,
        `Refreshing connection :  ${JSON.stringify(connection)}`,
        null,
      );
      return null;
    }
  }

  async createUserToken(): Promise<GlossBasiqTokenResponse> {
    try {
      const path = basiqConfig.endpoint.token;
      return await this.dataRepositoryService.send("GET", path, null, true, true, this.glossApiUrl);
    } catch (error) {
      await this.glossSyncService.logError([], error, `Creating user token`, null);
      throw error;
    }
  }
}
